import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Clock, BookOpen, Users, TrendingUp } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

// Reusable UI Components
const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white p-6 rounded-xl shadow-sm">
    <div className="flex items-center gap-4">
      <div className="p-3 bg-blue-50 rounded-lg">
        <Icon className="text-blue-900" size={24} />
      </div>
      <div>
        <p className="text-gray-600 text-sm">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
    </div>
  </div>
);

const LoadingSpinner = () => (
  <div className="flex items-center justify-center p-8">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900"></div>
  </div>
);

const EmptyState = ({ message }) => (
  <div className="flex flex-col items-center justify-center p-8 text-center">
    <BookOpen className="text-gray-400 mb-4" size={48} />
    <p className="text-gray-600">{message}</p>
  </div>
);

const CourseCard = ({ course }) => {
  const navigate = useNavigate();
  
  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    const durationParts = duration.match(/(\d+):(\d+):(\d+)/);
    if (!durationParts) return duration;
    const [, hours, minutes] = durationParts;
    return `${hours}h ${minutes}m`;
  };

  const handleCardClick = (e) => {
    // Prevent navigation if click was on the enroll button
    if (!e.target.closest('button')) {
      navigate(`/courses/${course.id}`);
    }
  };

  return (
    <div 
      className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group cursor-pointer"
      onClick={handleCardClick}
    >
      <div className="relative">
        <img 
          src={course.thumbnail_url || "/api/placeholder/400/250"}
          alt={course.title} 
          className="w-full h-48 object-cover rounded-t-xl group-hover:brightness-90 transition-all"
        />
        <div className="absolute top-4 right-4">
          <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium text-blue-900">
            {course.level}
          </span>
        </div>
      </div>
      <div className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm text-gray-600 flex items-center gap-1">
            <Clock size={14} />
            {formatDuration(course.duration)}
          </span>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-900 transition-colors">
          {course.title}
        </h3>
        <p className="text-gray-600 mb-4 line-clamp-2">{course.description}</p>
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="flex items-center gap-1">
            <span className="font-medium text-blue-900">
              {course.price ? `$${course.price}` : 'Free'}
            </span>
          </div>
          <button 
            className={`px-6 py-2 rounded-lg transition-colors ${
              course.status === 'published'
                ? 'bg-blue-900 text-white hover:bg-blue-800'
                : 'bg-gray-100 text-gray-600 cursor-not-allowed'
            }`}
            disabled={course.status !== 'published'}
            aria-label={`${course.status === 'published' ? 'Enroll in' : 'Coming soon:'} ${course.title}`}
          >
            {course.status === 'published' ? 'Enroll Now' : 'Coming Soon'}
          </button>
        </div>
      </div>
    </div>
  );
};

const CoursesPage = () => {
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const coursesResponse = await supabase.from('courses').select('*');
        const categoriesResponse = await supabase.from('categories').select('*');

        if (coursesResponse.error) throw new Error(coursesResponse.error.message);
        if (categoriesResponse.error) throw new Error(categoriesResponse.error.message);

        setCourses(coursesResponse.data || []);
        setCategories(categoriesResponse.data || []);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const stats = [
    { 
      icon: BookOpen, 
      label: 'Total Courses', 
      value: courses.length 
    },
    { 
      icon: Users, 
      label: 'Published Courses', 
      value: courses.filter(c => c.status === 'published').length 
    },
    { 
      icon: TrendingUp, 
      label: 'Free Courses', 
      value: courses.filter(c => !c.price).length 
    }
  ];

  const allCategories = [
    { id: 'all', name: 'All Courses' },
    ...(categories || []).map(cat => ({
      id: cat.id,
      name: cat.name
    }))
  ];

  const filteredCourses = courses.filter(course => {
    const matchesSearch = (course.title?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
                         (course.description?.toLowerCase() || '').includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || course.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg max-w-md text-center">
          <p className="font-medium">Error loading courses</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <main className="bg-gradient-to-b from-gray-50 to-white min-h-screen">
      <div className="max-w-7xl mx-auto p-8">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Course Catalog</h1>
            <p className="text-gray-600">Discover our comprehensive range of sales courses</p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full md:w-auto">
            <div className="relative flex-grow sm:flex-grow-0">
              <input
                type="text"
                placeholder="Search courses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full sm:w-64 px-4 py-2 pl-10 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-900/20 focus:border-blue-900 transition-all"
                aria-label="Search courses"
              />
              <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
            </div>
            <button 
              className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              aria-label="Open filters"
            >
              <Filter size={20} />
              Filters
            </button>
          </div>
        </div>

        {/* Stats Section */}
        {!loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {stats.map((stat, index) => (
              <StatCard key={index} {...stat} />
            ))}
          </div>
        )}

        {/* Categories Section */}
        <div className="flex gap-4 mb-8 overflow-x-auto pb-2">
          {allCategories.map(category => (
            <button
              key={category.id}
              className={`px-6 py-2.5 rounded-lg whitespace-nowrap transition-all duration-300 font-medium ${
                selectedCategory === category.id
                  ? 'bg-blue-900 text-white shadow-lg shadow-blue-900/20'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
              onClick={() => setSelectedCategory(category.id)}
              aria-pressed={selectedCategory === category.id}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Courses Grid */}
        {loading ? (
          <LoadingSpinner />
        ) : filteredCourses.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCourses.map(course => (
              <CourseCard key={course.id} course={course} />
            ))}
          </div>
        ) : (
          <EmptyState 
            message={
              searchQuery || selectedCategory !== 'all'
                ? "No courses match your search criteria"
                : "No courses available yet"
            }
          />
        )}
      </div>
    </main>
  );
};

export default CoursesPage;
